import { ReactNode, useCallback, useId, useMemo, useState } from "react";
import {
  Control,
  Controller,
  FieldValues,
  Path,
  RegisterOptions,
} from "react-hook-form";

import { cn } from "../../utls/utils";
import Typography from "../typography";

export interface ThemedButtonGroupOption {
  value: string;
  label: string;
  description?: string;
  icon?: ReactNode;
  disabled?: boolean;
}

export type ThemedButtonGroupLayout = "horizontal" | "vertical";

export interface ThemedButtonGroupProps {
  /** Accessible label for the radio group. */
  label?: string;
  /** Supporting helper text rendered below the control when there is no error. */
  helperText?: ReactNode;
  /** Error text rendered below the control. */
  error?: ReactNode;
  /** Options rendered as radio-style buttons. */
  options: ThemedButtonGroupOption[];
  /** Currently selected value. When provided the component becomes controlled. */
  value?: string;
  /** Default value for uncontrolled usage. */
  defaultValue?: string;
  /** Called whenever the selected value changes. */
  onChange?: (value: string) => void;
  /** Layout direction for the group. */
  layout?: ThemedButtonGroupLayout;
  /** Whether each button should stretch to fill the available width. */
  fullWidth?: boolean;
  /** Disable all options in the group. */
  disabled?: boolean;
  /** Additional class name for the outer wrapper. */
  className?: string;
  /** Additional class name for every option button. */
  optionClassName?: string;
  /** Optional id used to connect the accessible label. */
  id?: string;
}

const layoutClasses: Record<ThemedButtonGroupLayout, string> = {
  horizontal: "flex flex-wrap gap-2",
  vertical: "flex flex-col gap-3",
};

function resolveInitialValue(
  value: string | undefined,
  defaultValue: string | undefined,
  options: ThemedButtonGroupOption[]
) {
  if (value !== undefined) {
    return value;
  }

  if (defaultValue !== undefined) {
    return defaultValue;
  }

  return options[0]?.value ?? "";
}

function OptionButton({
  option,
  isSelected,
  onSelect,
  disabled,
  fullWidth,
  className,
}: {
  option: ThemedButtonGroupOption;
  isSelected: boolean;
  onSelect: () => void;
  disabled: boolean;
  fullWidth: boolean;
  className?: string;
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={isSelected}
      onClick={() => {
        if (!disabled && !option.disabled) {
          onSelect();
        }
      }}
      disabled={disabled || option.disabled}
      className={cn(
        "relative flex items-start gap-3 rounded-xl border px-4 py-3 text-left transition", 
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-terracotta-300 focus-visible:ring-offset-2",
        isSelected
          ? "border-terracotta-500 bg-terracotta-50 text-terracotta-700 shadow-sm"
          : "border-beige-300 bg-white text-brown-700 hover:border-terracotta-400 hover:bg-cream-50",
        disabled || option.disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer",
        fullWidth ? "w-full" : "",
        className
      )}
    >
      {option.icon ? (
        <span aria-hidden className="mt-0.5 flex h-5 w-5 items-center justify-center text-terracotta-600">
          {option.icon}
        </span>
      ) : null}
      <span className="flex flex-col">
        <span className="text-sm font-medium">{option.label}</span>
        {option.description ? (
          <span className="mt-1 text-xs text-brown-500">{option.description}</span>
        ) : null}
      </span>
    </button>
  );
}

export function ThemedButtonGroup({
  label,
  helperText,
  error,
  options,
  value,
  defaultValue,
  onChange,
  layout = "horizontal",
  fullWidth = false,
  disabled = false,
  className,
  optionClassName,
  id,
}: ThemedButtonGroupProps) {
  const generatedId = useId();
  const groupId = id ?? generatedId;
  const labelId = `${groupId}-label`;
  const messageId = `${groupId}-message`;

  const [internalValue, setInternalValue] = useState(() =>
    resolveInitialValue(value, defaultValue, options)
  );

  const isControlled = value !== undefined;

  const resolvedValue = isControlled ? value : internalValue;

  const handleSelect = useCallback(
    (nextValue: string) => {
      if (!isControlled) {
        setInternalValue(nextValue);
      }
      onChange?.(nextValue);
    },
    [isControlled, onChange]
  );

  const descriptionNode = useMemo(() => {
    if (error) {
      return (
        <Typography
          id={messageId}
          variant="caption"
          className="mt-1 text-danger"
        >
          {error}
        </Typography>
      );
    }

    if (helperText) {
      return (
        <Typography
          id={messageId}
          variant="caption"
          className="mt-1 text-terracotta-600"
        >
          {helperText}
        </Typography>
      );
    }

    return null;
  }, [error, helperText, messageId]);

  return (
    <div className={cn("flex flex-col", className)}>
      {label ? (
        <Typography
          id={labelId}
          variant="subtitle2"
          className="mb-2 text-terracotta-700"
        >
          {label}
        </Typography>
      ) : null}
      <div
        role="radiogroup"
        aria-labelledby={label ? labelId : undefined}
        aria-describedby={descriptionNode ? messageId : undefined}
        className={layoutClasses[layout]}
      >
        {options.map((option) => (
          <OptionButton
            key={option.value}
            option={option}
            isSelected={resolvedValue === option.value}
            onSelect={() => handleSelect(option.value)}
            disabled={disabled}
            fullWidth={fullWidth}
            className={optionClassName}
          />
        ))}
      </div>
      {descriptionNode}
    </div>
  );
}

export interface ThemedButtonGroupFieldProps<
  TFieldValues extends FieldValues,
> extends Omit<
    ThemedButtonGroupProps,
    "value" | "defaultValue" | "onChange"
  > {
  name: Path<TFieldValues>;
  control: Control<TFieldValues>;
  rules?: RegisterOptions<TFieldValues, Path<TFieldValues>>;
}

export function ThemedButtonGroupField<
  TFieldValues extends FieldValues,
>({ name, control, rules, ...props }: ThemedButtonGroupFieldProps<TFieldValues>) {
  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({ field: { value, onChange }, fieldState }) => (
        <ThemedButtonGroup
          {...props}
          value={value as string | undefined}
          onChange={onChange}
          error={fieldState.error?.message ?? props.error}
        />
      )}
    />
  );
}

export default ThemedButtonGroup;
